import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// `withCredentials` sends the HttpOnly `access_token` cookie on every call.
// `Authorization: Bearer <token>` is still attached from localStorage for
// backward compatibility with sessions that predate the cookie migration.
const api = axios.create({ baseURL: API, withCredentials: true });

const NON_MUTATING_METHODS = new Set(['get', 'head', 'options']);

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Attach JWT from localStorage AND X-CSRF-Token header for mutating requests.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cp_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (!NON_MUTATING_METHODS.has(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf && !config.headers['X-CSRF-Token']) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Global 401 handler -> clear auth and redirect to /login (skip on public routes).
const PUBLIC_ROUTE_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password',
  '/supplier/accept', '/verify', '/transparency', '/legal', '/pilot',
  '/pricing', '/methodology'];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      const onPublic = PUBLIC_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
      if (!onPublic) {
        localStorage.removeItem('cp_token');
        localStorage.removeItem('cp_user');
        localStorage.removeItem('cp_org');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const productsAPI = {
  getAll: () => api.get('/products'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const suppliersAPI = {
  getAll: () => api.get('/suppliers'),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

export const invoicesAPI = {
  getAll: () => api.get('/invoices'),
  create: (data) => api.post('/invoices', data),
  upload: (file, meta = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('energy_type', meta.energy_type || 'Electricity');
    fd.append('consumption', meta.consumption ?? 0);
    fd.append('cost', meta.cost ?? 0);
    fd.append('date', meta.date || '');
    return api.post('/invoices/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id) => api.get(`/invoices/${id}/file`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/invoices/${id}`),
  processAll: () => api.post('/invoices/process'),
};

export const pcfAPI = {
  getProducts: () => api.get('/pcf/products'),
  getDetail: (productId) => api.get(`/pcf/${productId}`),
  calculate: (productId) => api.post(`/pcf/calculate/${productId}`),
  listReports: () => api.get('/pcf-reports'),
  exportPdf: (productId) => api.get(`/pcf/${productId}/export-pdf`, { responseType: 'blob' }),
  exportAuditPdf: (productId) => api.get(`/pcf/${productId}/export-audit-pdf`, { responseType: 'blob' }),
  exportEuReport: (productId) => api.get(`/pcf/${productId}/export-eu-report`, { responseType: 'blob' }),
};

export const unifiedAccountingAPI = {
  getData: () => api.get('/unified-accounting'),
  reallocate: () => api.post('/unified-accounting/reallocate'),
};

export const livingPcfAPI = {
  getData: () => api.get('/living-pcf'),
};

export const cfoDashboardAPI = {
  getData: () => api.get('/cfo-dashboard'),
};

export const cpoDashboardAPI = {
  getData: () => api.get('/cpo-dashboard'),
};

export const csoDashboardAPI = {
  getData: () => api.get('/cso-dashboard'),
};

export const apiKeysAPI = {
  list: () => api.get('/api-keys'),
  create: (name, scopes = ['read']) => api.post('/api-keys', { name, scopes }),
  delete: (key) => api.delete(`/api-keys/${key}`),
  usage: (key) => api.get(`/api-keys/${key}/usage`),
  activity: (key, limit = 50) => api.get(`/api-keys/${key}/activity?limit=${limit}`),
};

export const transparencyAPI = {
  methodology: () => api.get('/transparency/methodology'),
  factors: (params = {}) => api.get('/transparency/factors', { params }),
  changelog: () => api.get('/transparency/changelog'),
  recentEdits: (limit = 5) => api.get(`/transparency/recent-edits?limit=${limit}`),
};

export const approvalsAPI = {
  list: (productId) => api.get(`/approval-logs/${productId}`),
  create: (productId, action, comments = '') => api.post(`/approval-logs/${productId}`, { action, comments }),
};

export const verifyAPI = {
  issueToken: (productId, ttl_days = 30) => api.post(`/verify/${productId}/token`, null, { params: { ttl_days } }),
};

export const adminOpsAPI = {
  rateLimits: () => api.get('/admin/rate-limits'),
  emailLogs: (params = {}) => api.get('/admin/email-logs', { params }),
  emailStats: () => api.get('/admin/email-logs/stats'),
  emailResend: (logId) => api.post('/admin/email-logs/resend', { log_id: logId }),
  emailStatus: () => api.get('/admin/email-status'),
  sendTestEmail: (email) => api.post('/admin/send-test-email', { email }),
};

export const changeLogsAPI = {
  list: (params = {}) => api.get('/change-logs', { params }),
  byRecord: (recordId) => api.get(`/change-logs/${recordId}`),
};

export const tuvDocsAPI = {
  list: () => api.get('/tuv-docs'),
  get: (docId) => api.get(`/tuv-docs/${docId}`),
  downloadZipUrl: () => `${API}/admin/download-tuv-docs`,
};

export const ics2API = {
  sample: () => api.get('/ics2/sample'),
  preview: (payload) => api.post('/ics2/preview', payload),
  generate: (payload) => api.post('/ics2/generate', payload, { responseType: 'blob' }),
};

export const shipmentsAPI = {
  list: () => api.get('/shipments'),
  get: (id) => api.get(`/shipments/${id}`),
  create: (payload) => api.post('/shipments', payload),
  update: (id, payload) => api.put(`/shipments/${id}`, payload),
  delete: (id) => api.delete(`/shipments/${id}`),
  previewXml: (id) => api.get(`/shipments/${id}/preview-xml`),
  generateXml: (id) => api.post(`/shipments/${id}/generate-xml`, null, { responseType: 'blob' }),
  linkReport: (id, reportId) => api.post(`/shipments/${id}/link-report`, { report_id: reportId }),
};

export const leadsAPI = {
  create: (payload) => api.post('/leads', payload),
};

export const checkoutAPI = {
  packages: () => api.get('/checkout/packages'),
  createSession: (packageId, originUrl) => api.post('/checkout/session', {
    package_id: packageId,
    origin_url: originUrl || window.location.origin,
  }),
  status: (sessionId) => api.get(`/checkout/status/${sessionId}`),
  // Lemon Squeezy annual-subscription checkout (replaces Stripe at go-live)
  createLemonSqueezySession: (tier) => api.post('/checkout/lemon-squeezy', { tier }),
};

export const notificationsAPI = {
  list: (limit = 20, onlyUnread = false) => api.get(`/notifications?limit=${limit}&only_unread=${onlyUnread}`),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
};

export const billingAPI = {
  createPortalSession: (returnUrl) => api.post('/billing/create-portal-session', {
    return_url: returnUrl || `${window.location.origin}/settings`,
  }),
};

export default api;
